import { CertificatesPage } from 'pages/Certificates';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PreviewContext } from 'contexts/previewContext';
import { STUDIO_PREVIEW_PRODUCT_MESSAGE, type StudioPreviewProduct } from '@repo/types';
import { Box, ThemeProvider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { creteMuiTheme } from 'settings/mui/createMuiTheme';
import { Theme } from 'settings/mui/palette';
import { CertificatePage } from 'pages/CertificatePage';
import ClaimOwnershipSignUp from 'pages/onboarding/ClaimOwnershipSignUp';
import SignupPage from 'pages/Signup';
import { CertificatePageSkeleton } from 'components/pages/certificates/productPage/CertificatePageSkeleton';
import { flushSync } from 'react-dom';
import { spotlightRingCss, SPOTLIGHT_PREVIEW_MIN_RADIUS } from '@repo/ui';
import { getShadowRoot } from 'src';
import {
  clearTokenHighlight,
  measureTokenHighlight,
  type HighlightTheme,
  type StudioTokenName,
} from './tokenHighlight';

/**
 * VRG-549: the shared product-tour spotlight, resolved once.
 *
 * `contrastHairline` because this highlight does not dim the page behind it (the operator
 * is judging colours, so a scrim would distort the thing under review) — see the docblock
 * on `packages/ui/theme/spotlight.ts`.
 */
const HIGHLIGHT_RING_CSS = spotlightRingCss({
  offset: 2,
  contrastHairline: true,
  important: true,
});

export const StudioPreviewPage = () => {
  const [hasLoadedFirstTheme, setHasLoadedFirstTheme] = useState(false);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [pageToPreview, setPageToPreview] = useState<string | null>(null);

  const { setIsPreview, setHidePoweredBy, setPreviewPublicView, setPreviewEmbedded, setPreviewCertificateId, setPreviewProduct } = useContext(PreviewContext);
  const queryClient = useQueryClient();
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  // When interactive, let the visitor click through the app (VERA-268); the
  // default (read-only) mode still locks navigation for a static theme preview.
  const interactive = url.searchParams.get('interactive') === 'true';

  useEffect(() => {
    setIsPreview(true);

    //invalidate all queries
    setTimeout(() => {
      queryClient.invalidateQueries();
    }, 200);

    const shadowRoot = getShadowRoot();
    const targetElement = shadowRoot || document;

    // Hide scrollbar globally for preview mode
    const style = document.createElement('style');
    style.textContent = `
      /* Hide scrollbar for Chrome, Safari and Opera */
      *::-webkit-scrollbar {
        display: none;
      }
      
      /* Hide scrollbar for IE, Edge and Firefox */
      * {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
      }
    `;

    if (shadowRoot) {
      // Add style to shadow root
      shadowRoot.appendChild(style);
    } else {
      // Fallback to document head
      document.head.appendChild(style);
    }

    // Prevent all link clicks and navigation in preview mode
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check if clicked element or any parent is a link or button with navigation
      const link = target.closest('a, [role="link"], button[type="button"]');
      if (link) {
        // Allow only if it's a pure UI button (like expand/collapse)
        const isUIButton = target.closest('[data-allow-preview]');

        if (!isUIButton) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    // Prevent form submissions
    const handleSubmit = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // In read-only mode, capture all clicks and form submissions to lock the
    // preview. In interactive mode we skip this so the visitor can click through.
    if (!interactive) {
      targetElement.addEventListener('click', handleClick, true);
      targetElement.addEventListener('submit', handleSubmit, true);
    }

    // Cleanup
    return () => {
      if (shadowRoot) {
        shadowRoot.removeChild(style);
      } else {
        document.head.removeChild(style);
      }
      if (!interactive) {
        targetElement.removeEventListener('click', handleClick, true);
        targetElement.removeEventListener('submit', handleSubmit, true);
      }
    };
  }, [queryClient, interactive]);

  /*
    VRG-549: the token Studio is currently asking us to highlight, or null.

    Held in a ref rather than state because nothing in this component RENDERS from it —
    the highlight is an injected stylesheet, not markup — and putting it in state would
    re-render the whole previewed flow on every hover of a Studio control, which is the
    one thing a live preview must not do while someone is judging it.
  */
  const highlightTokenRef = useRef<StudioTokenName | null>(null);
  /*
    The live theme, for the same reason and read from the same place the highlight's
    VERIFICATION needs it: a candidate element only counts if it is actually painting the
    token's current value, so the refresh needs the value, not just the token name. Kept
    in a ref alongside the state so the message handler can read it without re-binding.
  */
  const themeRef = useRef<Theme | null>(null);
  /** Debounce handle for the re-measure that follows a theme push. See its call site. */
  const remeasureTimer = useRef<number | undefined>(undefined);

  /**
   * Re-run the highlight and tell Studio what it matched.
   *
   * Called on the highlight message AND after each theme push, because a theme change both
   * re-renders the flow and changes what "is this element painting the token" answers — a
   * count taken once would go stale under the operator's hands as they drag a picker.
   */
  const refreshHighlight = useCallback(() => {
    const token = highlightTokenRef.current;
    const live = themeRef.current;

    /*
      The probe renderer the differential needs (see `tokenHighlight.ts`).

      `flushSync` is load-bearing, not a style choice. The measurement renders the flow
      twice with the token perturbed to sentinel values; a normal `setTheme` would schedule
      those renders for a later task, so the browser would PAINT magenta before the
      snapshot ran and the snapshot would read the un-perturbed tree anyway. Forcing both
      renders inside this one task means the perturbed trees exist only between paints.

      `themeRef` is deliberately NOT written here. It tracks what Studio last pushed, and a
      probe is not a push — leaving it alone is what guarantees a throw mid-probe cannot
      strand the ref on a sentinel value.
    */
    const renderWithTheme = (next: HighlightTheme) => {
      flushSync(() => setTheme(next as Theme));
    };

    const { parts, invisible } = measureTokenHighlight(
      getShadowRoot(),
      token,
      live,
      renderWithTheme,
      HIGHLIGHT_RING_CSS,
      SPOTLIGHT_PREVIEW_MIN_RADIUS,
    );

    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'studio-highlight-result', token, parts, invisible },
        '*',
      );
    }
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'theme') {
        setTheme(event.data.theme);
        themeRef.current = event.data.theme;
        // VERA-644 (TD-066 last-mile): mirror the Studio WhiteLabelCard's live
        // (possibly unsaved) toggle so the iframed certificate preview's
        // Footer badge updates immediately, without a save round-trip.
        setHidePoweredBy(!!event.data.hidePoweredBy);
        // Which install location Studio is previewing. Absent means hosted, which is
        // the safe default: it shows the bar rather than silently hiding a brand's mark.
        setPreviewEmbedded(!!event.data.embedded);
        setHasLoadedFirstTheme(true);
        /*
          Re-measure after the repaint, not during it: the flow re-renders from the new
          theme, so measuring now would measure the tree that is about to be replaced.

          DEBOUNCED, unlike the highlight message below, because a theme push is not one
          event — Studio re-pushes on every frame of a colour-picker drag, and each
          measurement costs four synchronous renders of the whole flow. Waiting for the
          drag to settle costs nothing in accuracy: the differential's answer is the SET of
          elements the token reaches, and that set does not move when its value does. (That
          is a property the previous exact-value gate did not have — under it the count
          genuinely changed mid-drag, which is why it re-ran eagerly.)
        */
        if (highlightTokenRef.current) {
          window.clearTimeout(remeasureTimer.current);
          remeasureTimer.current = window.setTimeout(refreshHighlight, 200);
        }
      }
      /*
        VRG-827: the brand's own product to render inside the mock certificate.

        One more message type on a channel that already works, rather than new
        plumbing — the same move VRG-549 made for the highlight. The alternative was
        fetching the product here, which would need a public product endpoint, an
        auth story for a cross-origin iframe, and a loading state; the platform
        already HAS the product loaded, so sending it costs nothing.

        `product: null` clears back to the mock, which is what a brand with no
        products gets and what Studio sends if the selection is cleared.
      */
      if (event.data?.type === STUDIO_PREVIEW_PRODUCT_MESSAGE) {
        setPreviewProduct((event.data.product ?? null) as StudioPreviewProduct | null);
      }
      // VRG-549: "what does this control edit?" — Studio names a token, we outline what
      // it governs here and answer with a labelled count. `token: null` clears.
      if (event.data?.type === 'studio-highlight') {
        highlightTokenRef.current = (event.data.token ?? null) as StudioTokenName | null;
        window.clearTimeout(remeasureTimer.current);
        refreshHighlight();
      }
    };
    window.addEventListener('message', onMessage);
    // Handshake: tell the Studio parent we're mounted and ready for the theme.
    // Without this the parent's onLoad post can arrive before this listener
    // exists, leaving the preview stuck on its loader (VERA-267).
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'studio-preview-ready' }, '*');
    }
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(remeasureTimer.current);
      clearTokenHighlight(getShadowRoot());
    };
  }, [setHidePoweredBy, setPreviewEmbedded, setPreviewProduct, refreshHighlight]);

  useEffect(() => {
    setPageToPreview(url.searchParams.get('page'));
    // 'certificate-public' renders the SAME CertificatePage as 'certificate'; the only
    // difference is which viewer it resolves as, which this flag decides. Keeping one
    // component means the public preview cannot drift from the page it is previewing.
    setPreviewPublicView(url.searchParams.get('page') === 'certificate-public');
    // Optional. Absent -> the certificate page renders the mock, which is what Studio
    // wants when previewing a theme. Present -> it renders that real certificate, which
    // is what the platform's certificate preview wants. Set BEFORE the
    // invalidateQueries above lands so the refetch uses the right id.
    setPreviewCertificateId(url.searchParams.get('certificate_id'));
  }, []);

  const renderPreviewPage = () => {
    switch (pageToPreview) {
      case 'certificates':
        return <CertificatesPage />;
      case 'certificate':
      case 'certificate-public':
        return <CertificatePage />;
      /*
        Studio's dropdown labels this flow "Claim ownership", and this now renders the
        page that flow actually is. It used to render `pages/CertificateAccess` — the
        email ACCESS GATE — which was never claim-ownership at all, so a brand tuning
        their theme against "Claim ownership" was styling a different screen from the
        one their recipients open. That page has been deleted (the gate is a state card
        on the certificate, not a route — see `App.tsx`), which forced the question and
        surfaced the mis-mapping.

        ⚠️ WITHOUT `?email=` and `?certificate_id=` on the preview URL this renders the
        page's `UnusableClaimLink` terminal state, not the invitation. That is still a
        real, themed screen — every token Studio can edit paints it — but it is not the
        step a recipient sees. Passing those two params through from the platform's
        preview URL is the fix, and it belongs in `apps/platform`'s Studio preview
        config, outside this app.
      */
      case 'claim-ownership':
        return <ClaimOwnershipSignUp />;
      case 'purchase-product':
        return <SignupPage />;
      default:
        return <div>Invalid preview page</div>;
    }
  };

  const shadowRoot = getShadowRoot();

  return (
    <ThemeProvider theme={creteMuiTheme(theme, shadowRoot)}>
      {/*
        The REAL app never shows a VERA loader here — CertificatePage renders
        <CertificatePageSkeleton /> while it loads (§19: skeletons, not spinners). This
        harness was showing a brand loader that exists nowhere in the product, so the
        preview was misrepresenting the very thing it exists to represent.

        The gate itself stays: rendering before the first theme arrives would flash the
        default palette and then repaint in the brand's. What changes is WHAT fills that
        window — the same skeleton a visitor sees. Pages with no skeleton of their own
        render nothing rather than borrowing the certificate's, which would be a different
        lie.
      */}
      {/* Read from the URL, not `pageToPreview` — that state is set in an effect and so is
          null on the first render, which would blank the frame for a tick before the
          skeleton appeared. The query string is available synchronously. */}
      {!hasLoadedFirstTheme && (url.searchParams.get('page') === 'certificate' || url.searchParams.get('page') === 'certificate-public') && (
        <CertificatePageSkeleton />
      )}
      {hasLoadedFirstTheme && (
        <Box
          sx={
            interactive
              ? {}
              : {
                  userSelect: 'none',
                  '& a, & [role="link"], & button': {
                    cursor: 'default !important',
                    pointerEvents: 'auto',
                  },
                  '& a:hover, & [role="link"]:hover': {
                    textDecoration: 'none !important',
                    opacity: 1,
                  },
                  '& button, & button *, & [role="button"], & [role="button"] *': {
                    transition: 'none !important',
                  },
                  '& button:hover, & [role="button"]:hover &:not([data-allow-preview])': {
                    transform: 'none !important',
                    scale: '1 !important',
                    filter: 'none !important',
                  },
                  '& button:active, & [role="button"]:active': {
                    transform: 'none !important',
                    scale: '1 !important',
                    filter: 'none !important',
                  },
                  '& button:focus, & [role="button"]:focus': {
                    outline: 'none !important',
                  },
                  '[data-allow-preview]': {
                    cursor: 'pointer !important',
                    pointerEvents: 'auto !important',
                  },
                }
          }
        >
          {renderPreviewPage()}
        </Box>
      )}
    </ThemeProvider>
  );
};
